import React, { useEffect, useState } from 'react';

import { Box, Grid, Typography } from '@mui/material';
import { FormikValues } from 'formik';

import {
  GA_EVENTS,
  getEmail,
  getTypeOfUser,
  getUserProps,
  TypeOfUser,
} from '@pharmaplan/common';

import useProfessional from '../../hooks/Professional/useProfessional';
import useSignupFlow from '../../hooks/useSignupFlow/useSignupFlow';
import { useAppDispatch } from '../../hooks/useAppDispatch';
import { useAppSelector } from '../../hooks/useAppSelector';
import useAnalytics from '../../analytics/useAnalytics';
import { setData } from '../../actions/persistActions';
import strings from '../../localization';

import PharmacistSignupFlow from './PharmacistSignupFlow';
import PharmacySignupFlow from './PharmacySignupFlow';
import SignupStepper from './SignupStepper';
import { toAnalyticsStepNumber } from './helper';
import styles from './styles';

const { pharmacy } = TypeOfUser;

const SignupFlow = () => {
  const dispatch = useAppDispatch();

  const analytics = useAnalytics();

  const { selfUserType } = useProfessional();

  const [step, setStep] = useState(0);

  const userType = useAppSelector(getTypeOfUser);
  const userProps = useAppSelector(getUserProps);
  const email = useAppSelector(getEmail);

  const isPharmacy = userType === pharmacy;

  let titleLabel = strings.professional;

  if (isPharmacy) {
    titleLabel = strings.pharmacy;
  }

  const title = strings.formatString(
    strings.signupAs,
    titleLabel,
  );

  const changeStep = (steps: object, values: FormikValues, type: string) => {
    // USER MOVED TO NEXT STEP
    analytics?.logCustomEvent({
      event: GA_EVENTS.signupProgress,
      step_name: (steps?.[step as keyof typeof steps] as { screenName: string })
        ?.screenName,
      step_number: toAnalyticsStepNumber(step),
    });

    dispatch(
      setData({
        userType: selfUserType,
        type,
        data: { ...values },
        email,
      }),
    );

    if (step < Object.keys(steps).length - 1) {
      setStep(step + 1);
    }

    return null;
  };

  // USER INITIATED SIGNUP
  useEffect(() => {
    analytics?.logCustomEvent({
      event: GA_EVENTS.signup,
      method: userProps?.loginType,
      role: userType,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const userProfile = () => {
    if (isPharmacy) {
      return (
        <PharmacySignupFlow
          setStep={setStep}
          step={step}
          changeStep={changeStep}
        />
      );
    }

    return (
      <PharmacistSignupFlow
        setStep={setStep}
        step={step}
        changeStep={changeStep}
      />
    );
  };

  useSignupFlow();

  return (
    <Box sx={styles.boxContainer}>
      <Grid lg={8} sx={styles.container} container direction="column">
        <Typography color="primary" sx={styles.title}>
          {title}
        </Typography>

        <SignupStepper step={step} setStep={setStep} />
        {userProfile()}
      </Grid>
    </Box>
  );
};

export default SignupFlow;
